import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';

// Graph availability (which kinds double as static shape parameters) is
// injected from the engine's GEOMETRY_NODE_KINDS in renderNodeDefs.ts.

const BLEND_COLOR_MODE_OPTIONS = [
  { label: 'Normal', value: 'normal' },
  { label: 'Multiply', value: 'multiply' },
  { label: 'Screen', value: 'screen' },
  { label: 'Overlay', value: 'overlay' },
  { label: 'Add', value: 'add' },
  { label: 'Subtract', value: 'subtract' },
  { label: 'Difference', value: 'difference' },
  { label: 'Min', value: 'min' },
  { label: 'Max', value: 'max' },
] as const;





export const MATH_NODE_DEFS: RenderNodeDefsSubset<
  | 'Add'
  | 'Subtract'
  | 'Multiply'
  | 'Divide'
  | 'Negate'
  | 'Absolute'
  | 'Sin'
  | 'Cos'
  | 'Atan2'
  | 'Power'
  | 'SquareRoot'
  | 'Min'
  | 'Max'
  | 'Clamp'
  | 'Mix'
  | 'MixColor'
  | 'CoverageContrast'
  | 'BlendColor'
  | 'TextRunColor'
  | 'Smoothstep'
  | 'Fraction'
  | 'Floor'
  | 'Ceil'
  | 'Round'
  | 'Modulo'
> = {
  Add: defineRenderNode({
    label: 'Add', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Subtract: defineRenderNode({
    label: 'Subtract', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Multiply: defineRenderNode({
    label: 'Multiply', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Divide: defineRenderNode({
    label: 'Divide', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Negate: defineRenderNode({
    label: 'Negate', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Absolute: defineRenderNode({
    label: 'Absolute', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Sin: defineRenderNode({
    label: 'Sin', cat: 'math', subcat: 'trigonometry',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Cos: defineRenderNode({
    label: 'Cos', cat: 'math', subcat: 'trigonometry',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Atan2: defineRenderNode({
    label: 'Atan2', cat: 'math', subcat: 'trigonometry',
    inputs: [{ name: 'y', type: 'float' }, { name: 'x', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Power: defineRenderNode({
    label: 'Power', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'base', label: 'Base', type: 'float' }, { name: 'exp', label: 'Exponent', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  SquareRoot: defineRenderNode({
    label: 'Square Root', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Min: defineRenderNode({
    label: 'Min', cat: 'math', subcat: 'range',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Max: defineRenderNode({
    label: 'Max', cat: 'math', subcat: 'range',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Clamp: defineRenderNode({
    label: 'Clamp', cat: 'math', subcat: 'range',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }, { name: 'min', label: 'Min', type: 'float' }, { name: 'max', label: 'Max', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Mix: defineRenderNode({
    label: 'Mix', cat: 'math', subcat: 'blend',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }, { name: 't', label: 'Factor', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  MixColor: defineRenderNode({
    label: 'Mix Color', cat: 'math', subcat: 'blend',
    inputs: [{ name: 'a', label: 'Color A', type: 'vec4' }, { name: 'b', label: 'Color B', type: 'vec4' }, { name: 't', label: 'Factor', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'vec4' }],
  }),
  // Skia's contrast over a text's coverage, tapered by the light of the ground
  // its ink guesses (crates/graph/src/nodes.rs coverage_contrast,
  // kb:decision/73): what a text's Mask fill passes through before the mix.
  CoverageContrast: defineRenderNode({
    label: 'Coverage Contrast', cat: 'math', subcat: 'blend',
    inputs: [
      { name: 'coverage', label: 'Coverage', type: 'float' },
      { name: 'ink', label: 'Ink', type: 'vec4' },
      { name: 'contrast', label: 'Contrast', type: 'float', range: { min: 0, max: 4, step: 0.01 } },
    ],
    outputs: [{ name: 'coverage', label: 'Coverage', type: 'float' }],
    graphs: ['material'],
  }),
  BlendColor: defineRenderNode({
    label: 'Blend Color', cat: 'math', subcat: 'blend',
    inputs: [
      { name: 'base', label: 'Base', type: 'vec4' },
      { name: 'blend', label: 'Blend', type: 'vec4' },
      { name: 'opacity', label: 'Opacity', type: 'float', range: { min: 0, max: 1, step: 0.01, subtype: 'percentage' } },
    ],
    outputs: [{ name: 'color', label: 'Color', type: 'vec4' }],
    graphs: ['material'],
    ui: [
      { kind: 'port', ref: 'base' },
      { kind: 'port', ref: 'blend' },
      { kind: 'port', ref: 'opacity' },
      { kind: 'select', field: 'mode', label: 'Mode', options: BLEND_COLOR_MODE_OPTIONS, default: 'normal' },
      { kind: 'port', ref: 'color' },
    ],
    minWidth: 176
  }),
  // The color a run of a text gives the glyphs at the sample, and `color`
  // where it gives none (fluveria-engine kb:task/438): what a text's
  // material mixes its ink by, so each run is drawn in its own color.
  TextRunColor: defineRenderNode({
    label: 'Text Run Color', cat: 'math', subcat: 'blend',
    inputs: [{ name: 'color', label: 'Color', type: 'vec4' }],
    outputs: [{ name: 'color', label: 'Color', type: 'vec4' }],
    graphs: ['material'],
  }),
  Smoothstep: defineRenderNode({
    label: 'Smoothstep', cat: 'math', subcat: 'blend',
    inputs: [{ name: 'edge0', label: 'Edge 0', type: 'float' }, { name: 'edge1', label: 'Edge 1', type: 'float' }, { name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Fraction: defineRenderNode({
    label: 'Fraction', cat: 'math', subcat: 'range',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Floor: defineRenderNode({
    label: 'Floor', cat: 'math', subcat: 'range',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Ceil: defineRenderNode({
    label: 'Ceil', cat: 'math', subcat: 'range',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  // Half away from nought, as FormatNumber rounds (not WGSL's round-to-even).
  Round: defineRenderNode({
    label: 'Round', cat: 'math', subcat: 'range',
    inputs: [{ name: 'x', label: 'Value', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  // Floored remainder with the sign of B: -1 mod 60 is 59; A mod 0 is A.
  Modulo: defineRenderNode({
    label: 'Modulo', cat: 'math', subcat: 'arithmetic',
    inputs: [{ name: 'a', label: 'Value A', type: 'float' }, { name: 'b', label: 'Value B', type: 'float' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  })
};
